import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useTranslation } from '../i18n';
import { FullPageLoader } from '../components/ui/spinner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShieldIcon } from '../components/icons';
import { NoIndex } from '../components/NoIndex';

interface ProtectedRouteProps {
  children: ReactNode;
  /** When true, the route additionally requires the `admin` role. */
  requireAdmin?: boolean;
}

/**
 * Client-side route guard.
 *
 * This is a convenience only — the Worker independently authorizes every API
 * call, so hiding a route never protects data on its own.
 */
export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { user, status } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  if (status === 'loading') {
    return <FullPageLoader label={t('guard.checkingSession')} />;
  }

  if (!user) {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  if (requireAdmin && user.role !== 'admin') {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <Card>
          <CardContent className="pt-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-danger-soft text-danger-soft-fg">
              <ShieldIcon className="h-6 w-6" />
            </div>
            <h1 className="mt-4 text-xl font-semibold text-fg">{t('guard.accessDenied.title')}</h1>
            <p className="mt-2 text-sm text-muted">{t('guard.accessDenied.body')}</p>
            <div className="mt-6 flex justify-center">
              <Link to="/dashboard">
                <Button variant="outline">{t('common.backToDashboard')}</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* Anything behind a session is kept out of search results, from one place. */}
      <NoIndex title={t('nav.dashboard')} />
      {children}
    </>
  );
}
